/**
 * /roster-confirm?token=… — the page a swing confirmation email links to.
 * No sign-in: the single-use token in the link is the credential. Opening the
 * page changes nothing (mail scanners open links); the employee presses
 * Accept or Decline. Mostly opened on a phone, so it is laid out for one.
 * Plan: docs/features/roster-swing-acceptance-plan.md (D-09)
 */
import { useEffect, useState } from 'react';
import { usePublicPageScroll } from '../../hooks/usePublicPageScroll';
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Button from '../shared/Button';
import LoadingSpinner from '../shared/LoadingSpinner';
import { SwingSummary, DeclineReasonField } from '../rosters/SwingConfirmCard';
import { swingDayLabel } from '../rosters/swingStatus';
import type { SwingDay, SwingStatus } from '../rosters/planner/plannerService';
import { apiUrl } from '../../config/apiConfig';
import { TEXT } from '../shared/Typography';

interface ConfirmView {
  firstName: string;
  siteName: string;
  start: string;
  end: string;
  status: SwingStatus;
  respondedAt?: string;
  usable: boolean;
  reason?: 'answered' | 'replaced' | 'expired';
  days: SwingDay[];
  disclaimer: string;
}

async function call(token: string, body?: unknown): Promise<ConfirmView> {
  const res = await fetch(apiUrl(`/api/roster-confirm/${encodeURIComponent(token)}`), {
    method: body ? 'POST' : 'GET',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success === false) throw new Error(json?.error || 'This link is not valid.');
  return json.data as ConfirmView;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-gray-50 px-4 py-8 pt-safe-mobile">
      <div className="mx-auto max-w-lg space-y-6">
        <img src="/grautomates-dark.png" alt="GR Petroleum Services" className="mx-auto h-12 w-auto" />
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">{children}</div>
      </div>
    </main>
  );
}

function Outcome({ tone, title, children }: { tone: 'green' | 'red' | 'amber'; title: string; children: React.ReactNode }) {
  const Icon = tone === 'green' ? CheckCircleIcon : tone === 'red' ? XCircleIcon : ExclamationTriangleIcon;
  const colour = tone === 'green' ? 'text-green-600' : tone === 'red' ? 'text-red-600' : 'text-amber-600';
  return (
    <div className="space-y-3 text-center">
      <Icon className={`mx-auto h-12 w-12 ${colour}`} aria-hidden />
      <h1 className={TEXT.titleLg}>{title}</h1>
      <div className="text-base text-gray-700">{children}</div>
    </div>
  );
}

export default function RosterConfirm() {
  usePublicPageScroll();
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [view, setView] = useState<ConfirmView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('This link is not complete. Please open it again from your email.');
      return;
    }
    call(token)
      .then(setView)
      .catch(e => setError(e instanceof Error ? e.message : 'This link is not valid.'));
  }, [token]);

  const answer = async (action: 'accept' | 'decline') => {
    setBusy(true);
    setSubmitError(null);
    try {
      setView(await call(token, { action, reason: action === 'decline' ? reason.trim() : undefined }));
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Could not save your answer. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <Shell>
        <Outcome tone="amber" title="This link can't be used">
          <p>{error}</p>
          <p className={`mt-2 ${TEXT.bodyMuted}`}>If you think this is wrong, please contact your roster admin.</p>
        </Outcome>
      </Shell>
    );
  }
  if (!view) {
    return (
      <Shell>
        <LoadingSpinner message="Loading your swing…" />
      </Shell>
    );
  }

  if (!view.usable) {
    const dates = `${swingDayLabel(view.start, false)} – ${swingDayLabel(view.end)}`;
    if (view.status === 'accepted') {
      return (
        <Shell>
          <Outcome tone="green" title="Swing accepted">
            <p>Thanks{view.firstName ? `, ${view.firstName}` : ''}. Your {view.siteName} swing, {dates}, is confirmed.</p>
            <p className={`mt-2 ${TEXT.bodyMuted}`}>You can see it in My Shifts in the GRAutomates app.</p>
          </Outcome>
        </Shell>
      );
    }
    // Dismissed is a decline the roster admin has acknowledged — the same answer.
    if (view.status === 'declined' || view.status === 'dismissed') {
      return (
        <Shell>
          <Outcome tone="red" title="Swing declined">
            <p>Your roster admin has been told you can&apos;t work your {view.siteName} swing, {dates}, and will be in touch.</p>
          </Outcome>
        </Shell>
      );
    }
    return (
      <Shell>
        <Outcome tone="amber" title={view.reason === 'replaced' ? 'This link has been replaced' : 'This link has expired'}>
          <p>
            {view.reason === 'replaced'
              ? 'Your roster admin has sent you a newer email for this swing. Please use the link in that one.'
              : 'This swing can no longer be answered from this link. Please contact your roster admin.'}
          </p>
        </Outcome>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="space-y-5">
        <div>
          <h1 className={TEXT.titleLg}>Please confirm your swing</h1>
          {view.firstName && <p className="text-base text-gray-600">Hi {view.firstName}, can you work this swing?</p>}
        </div>
        <SwingSummary siteName={view.siteName} start={view.start} end={view.end} days={view.days} />

        {declining ? (
          <div className="space-y-4">
            <DeclineReasonField id="public-decline-reason" value={reason} onChange={setReason} />
            {submitError && <p role="alert" className="text-sm text-red-600">{submitError}</p>}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button size="lg" variant="danger" fullWidth disabled={busy || !reason.trim()} onClick={() => void answer('decline')}>
                Decline swing
              </Button>
              <Button size="lg" variant="outline" fullWidth disabled={busy} onClick={() => setDeclining(false)}>
                Back
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className={`rounded-lg bg-gray-50 p-3 ${TEXT.body}`}>{view.disclaimer}</p>
            {submitError && <p role="alert" className="text-sm text-red-600">{submitError}</p>}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button size="lg" variant="primary" fullWidth disabled={busy} onClick={() => void answer('accept')}>
                I accept this swing
              </Button>
              <Button size="lg" variant="outline" fullWidth disabled={busy} onClick={() => setDeclining(true)}>
                I can&apos;t work it
              </Button>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}
